// Re-export shared document field types so existing importers don't break
export type { DocState, PassportIdFields } from './docs/types';

import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DOC_CATEGORIES } from '../../../mocks/cases';
import type { DocumentCategory, DocumentReviewStatus, CriminalCertificate } from '../../../mocks/cases';
import { formatDate } from '../../../utils/dateFormat';
import type { DocState, PassportIdFields, EmploymentFields, DocCardHandlers } from './docs/types';
import { getFileIcon, getCategoryDisplayLabel } from './docs/utils';
import { categorySidebarIconClass, categorySidebarStatus } from '../requiredDocuments';
import { mvpApi } from '../../../services/api';

import IdentityCategory from './docs/IdentityCategory';
import ResidenceCategory from './docs/ResidenceCategory';
import CriminalCategory from './docs/CriminalCategory';
import AsylumCategory from './docs/AsylumCategory';
import EmploymentCategory from './docs/EmploymentCategory.js';
import FamilyCategory from './docs/FamilyCategory';
import VulnerabilityCategory from './docs/VulnerabilityCategory';
import OtherCategory from './docs/OtherCategory';
import DocumentViewerModal from './docs/DocumentViewerModal';

interface DocumentsTabProps {
  docs: DocState[];
  onDocsChange: (updated: DocState[]) => void;
  highlightDocId?: string | null;
  onClearHighlight?: () => void;
  showAsylumCategory?: boolean;
  showEmploymentCategory?: boolean;
  showFamilyCategory?: boolean;
  showVulnerabilityCategory?: boolean;
  asylumScenario?: string;
  subRoute?: string;
  prorrogaType?: string;
  arrivalDate?: string;
  criminalCertificates?: CriminalCertificate[];
  onCriminalCertificatesChange?: (updated: CriminalCertificate[]) => void;
  countriesOfResidence?: string[];
  onCountriesChange?: (countries: string[]) => void;
  passportIdFields?: PassportIdFields;
  onPassportIdChange?: (key: keyof PassportIdFields, value: string) => void;
  employmentFields?: EmploymentFields;
  onEmploymentFieldsChange?: (patch: Partial<EmploymentFields>) => void;
  oarCaseNumber?: string;
  onOarCaseNumberChange?: (value: string) => void;
  categoryReview: Record<DocumentCategory, DocumentReviewStatus>;
  onCategoryReviewChange: (category: DocumentCategory, status: DocumentReviewStatus) => void;
  userId?: string | number;
  sessionId?: string;
  documents?: unknown[];
  archivedDocuments?: unknown[];
  onRefresh?: () => void | Promise<void>;
}

export default function DocumentsTab({
  docs, onDocsChange,
  highlightDocId, onClearHighlight,
  showAsylumCategory = true,
  showEmploymentCategory = false,
  showFamilyCategory = false,
  showVulnerabilityCategory = false,
  asylumScenario = '',
  subRoute = '',
  prorrogaType = '',
  arrivalDate = '',
  criminalCertificates = [],
  onCriminalCertificatesChange,
  countriesOfResidence = [],
  onCountriesChange,
  passportIdFields,
  onPassportIdChange,
  employmentFields,
  onEmploymentFieldsChange,
  oarCaseNumber = '',
  onOarCaseNumberChange,
  categoryReview,
  onCategoryReviewChange,
  userId,
  sessionId,
}: DocumentsTabProps) {
  const { t } = useTranslation();
  const sidebarLabel = (catKey: DocumentCategory, fallback: string) => {
    if (catKey === 'passport') return t('advisorPortal.sectionIdentity');
    if (catKey === 'residence') return t('advisorPortal.docCat_proof_of_residency_name');
    if (catKey === 'criminal') return t('advisorPortal.docCat_criminal_record_name');
    if (catKey === 'asylum') return t('advisorPortal.historyDocAsylumDocuments');
    if (catKey === 'employment') return t('advisorPortal.historyEmployment');
    if (catKey === 'family') return t('advisorPortal.sectionFamily');
    if (catKey === 'vulnerability') return t('advisorPortal.historyVulnerabilityReport');
    if (catKey === 'other') return t('advisorPortal.docCat_other_name');
    return fallback;
  };
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [archivedDocs, setArchivedDocs] = useState<DocState[]>([]);
  const [addingToCategory, setAddingToCategory] = useState<DocumentCategory | null>(null);
  const [newDocType, setNewDocType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | 'archived'>('passport');
  const [viewerDoc, setViewerDoc] = useState<DocState | null>(null);
  const [viewerCriminal, setViewerCriminal] = useState<CriminalCertificate | null>(null);
  const [viewerFileName, setViewerFileName] = useState('');
  const [viewerTitle, setViewerTitle] = useState('Document review');
  const [viewerBlobUrl, setViewerBlobUrl] = useState('');
  const [viewerMimeType, setViewerMimeType] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState('');
  const [viewerZoom, setViewerZoom] = useState(1);

  const docRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const fakeSizes = ['1.2 MB', '2.4 MB', '890 KB', '3.1 MB', '1.7 MB', '560 KB', '4.2 MB', '2.0 MB'];
  const randomSize = () => fakeSizes[Math.floor(Math.random() * fakeSizes.length)];

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => () => {
    if (viewerBlobUrl) URL.revokeObjectURL(viewerBlobUrl);
  }, [viewerBlobUrl]);

  useEffect(() => {
    if (!highlightDocId) return;
    const el = docRefs.current[highlightDocId];
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); onClearHighlight?.(); }
  }, [highlightDocId, onClearHighlight]);

  const showToast = (msg: string) => setToast(msg);

  const closeViewer = () => {
    setViewerDoc(null);
    setViewerCriminal(null);
    setViewerFileName('');
    setViewerTitle('Document review');
    setViewerMimeType('');
    setViewerLoading(false);
    setViewerError('');
    setViewerZoom(1);
    setViewerBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const fetchPreviewBlob = async (id: string, rawFile?: File) => {
    if (rawFile instanceof File) {
      return { blob: rawFile, mimeType: rawFile.type || '' };
    }
    const res = await mvpApi.downloadAdminCaseDocumentFile(userId, sessionId, id);
    return { blob: res.data, mimeType: String(res.data?.type || '') };
  };

  const openViewerForDoc = async (doc: DocState) => {
    setViewerDoc(doc);
    setViewerCriminal(null);
    setViewerFileName(doc.uploadedFile ?? doc.name ?? 'document');
    setViewerTitle(doc.name || 'Document review');
    setViewerLoading(true);
    setViewerError('');
    setViewerZoom(1);
    setViewerBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    try {
      const { blob, mimeType } = await fetchPreviewBlob(String(doc.id), doc.rawFile);
      setViewerMimeType(mimeType);
      setViewerBlobUrl(URL.createObjectURL(blob));
    } catch {
      setViewerError('Preview unavailable for this file.');
    } finally {
      setViewerLoading(false);
    }
  };

  const openViewerForCriminal = async (country: string, cert: CriminalCertificate) => {
    const certRawFile = (cert as CriminalCertificate & { rawFile?: File }).rawFile;
    setViewerDoc(null);
    setViewerCriminal(cert);
    setViewerFileName(cert.uploadedFile || cert.requestProofFile || `${country}.pdf`);
    setViewerTitle(`${country} certificate`);
    setViewerLoading(true);
    setViewerError('');
    setViewerZoom(1);
    setViewerBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    try {
      const { blob, mimeType } = await fetchPreviewBlob(String(cert.id), certRawFile);
      setViewerMimeType(mimeType);
      setViewerBlobUrl(URL.createObjectURL(blob));
    } catch {
      setViewerError('Preview unavailable for this file.');
    } finally {
      setViewerLoading(false);
    }
  };

  const handleViewerDownload = async () => {
    const active = viewerDoc || viewerCriminal;
    if (!active) return;
    if (viewerBlobUrl) {
      const a = document.createElement('a');
      a.href = viewerBlobUrl;
      a.download = viewerFileName || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    try {
      const activeRawFile = (active as { rawFile?: File }).rawFile;
      const { blob } = await fetchPreviewBlob(String(active.id), activeRawFile);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = viewerFileName || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      showToast('Unable to download this document');
    }
  };

  const handleViewerReview = (status: DocumentReviewStatus) => {
    if (viewerDoc) {
      onDocsChange(
        docs.map((d) =>
          d.id === viewerDoc.id
            ? { ...d, reviewStatus: status, viewed: true, rejectionReason: status === 'rejected' ? d.rejectionReason : undefined }
            : d,
        ),
      );
    }
    if (viewerCriminal && onCriminalCertificatesChange) {
      onCriminalCertificatesChange(
        criminalCertificates.map((c) =>
          c.id === viewerCriminal.id
            ? { ...c, reviewStatus: status, rejectionReason: status === 'rejected' ? c.rejectionReason : undefined }
            : c,
        ),
      );
    }
    closeViewer();
  };

  const handleUpload = (docId: string, filename: string) => {
    onDocsChange(docs.map((d) => d.id === docId ? { ...d, uploaded: true, uploadedAt: new Date().toISOString().slice(0, 10), uploadedFile: filename, reviewStatus: 'pending', fileSize: randomSize(), rejectionReason: undefined } : d));
    showToast(`${filename} uploaded successfully`);
    if (viewingDocId === docId) setViewingDocId(null);
  };

  const handleDrop = (e: React.DragEvent, docId: string) => {
    e.preventDefault(); setDraggingId(null);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(docId, file.name);
  };

  const handleDelete = (docId: string) => {
    const doc = docs.find((d) => d.id === docId);
    if (doc) { setArchivedDocs((prev) => [...prev, { ...doc, reviewStatus: 'pending' as DocumentReviewStatus }]); onDocsChange(docs.filter((d) => d.id !== docId)); }
    setDeletingId(null);
    if (viewingDocId === docId) setViewingDocId(null);
    showToast('Document archived');
  };

  const openViewer = (docId: string) => {
    setViewingDocId((prev) => prev === docId ? null : docId);
    onDocsChange(docs.map((d) => d.id === docId ? { ...d, viewed: true } : d));
  };

  const commonCardProps: DocCardHandlers = {
    draggingId, deletingId,
    onOpen: openViewer,
    onDelete: handleDelete,
    onDeleteConfirm: (id: string) => setDeletingId(id),
    onDeleteCancel: () => setDeletingId(null),
    onDragOver: (e: React.DragEvent, id: string) => { e.preventDefault(); setDraggingId(id); },
    onDragLeave: () => setDraggingId(null),
    onDrop: handleDrop,
    onFileChange: (id: string, file: File) => handleUpload(id, file.name),
  };

  const uploadedCountForCategory = (catKey: DocumentCategory): number => {
    if (catKey === 'criminal') {
      const fromDocs = docs.filter((d) => d.category === 'criminal' && d.uploaded).length;
      const fromCerts = criminalCertificates.filter(
        (c) => c.uploaded || c.uploadedFile || c.requestProofFile,
      ).length;
      return Math.max(fromDocs, fromCerts);
    }
    return docs.filter((d) => d.category === catKey && d.uploaded).length;
  };

  const visibleCategories = (Object.keys(DOC_CATEGORIES) as DocumentCategory[]).filter((catKey) => {
    if (catKey === 'asylum' && !showAsylumCategory) return false;
    if (catKey === 'employment' && !showEmploymentCategory) return false;
    if (catKey === 'family' && !showFamilyCategory) return false;
    if (catKey === 'vulnerability' && !showVulnerabilityCategory) return false;
    return true;
  });

  useEffect(() => {
    if (selectedCategory !== 'archived' && !visibleCategories.includes(selectedCategory as DocumentCategory)) {
      setSelectedCategory(visibleCategories[0] ?? 'passport');
    }
  }, [visibleCategories, selectedCategory]);

  const currentCategoryData = useMemo(() => {
    if (selectedCategory === 'archived') {
      return {
        label: t('advisorPortal.docArchivedTitle'),
        iconClass: 'ri-archive-line text-slate-400 text-base shrink-0',
        statusText: String(archivedDocs.length),
        statusClass: 'text-slate-400',
      };
    }
    const cat = DOC_CATEGORIES[selectedCategory as DocumentCategory];
    if (!cat) {
      return {
        label: '',
        iconClass: '',
        statusText: '',
        statusClass: '',
      };
    }
    const label = sidebarLabel(selectedCategory as DocumentCategory, getCategoryDisplayLabel(selectedCategory as DocumentCategory, cat.label, prorrogaType));
    const uploadedCount = uploadedCountForCategory(selectedCategory as DocumentCategory);
    const side = categorySidebarStatus({
      files: uploadedCount > 0 ? Array.from({ length: uploadedCount }, () => ({})) : [],
      reviewStatus: categoryReview[selectedCategory as DocumentCategory] ?? 'pending',
      multiple: cat.multiple,
      optional: cat.optional,
    });
    const statusText = String(side.text ?? '');
    const iconClass = categorySidebarIconClass(side, uploadedCount);
    const showStatus =
      uploadedCount > 0 ||
      side.variant === 'rejected' ||
      (side.variant !== 'complete' && side.variant !== 'none' && side.variant !== 'awaiting');

    return {
      label,
      iconClass,
      statusText: showStatus ? statusText : '',
      statusClass: side.variant === 'rejected'
        ? 'text-rose-600'
        : uploadedCount > 0
          ? 'text-emerald-700'
          : 'text-slate-400',
    };
  }, [selectedCategory, categoryReview, archivedDocs.length, prorrogaType, docs, criminalCertificates, t]);

  /* ── Shared extra props passed  to every category ── */
  const categoryExtras = {
    docs, onDocsChange,
    handleUpload,
    commonCardProps,
    viewingDocId,
    highlightDocId,
    docRefs,
    addingToCategory,
    setAddingToCategory,
    newDocType,
    setNewDocType,
    asylumScenario,
    subRoute,
    prorrogaType,
  };

  return (
    <div className="min-w-0">
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white text-xs font-medium px-4 py-2.5 rounded-xl">{toast}</div>
      )}

      {/* Doc cards column */}
      <div className="min-w-0">
        <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">

          {/* Mobile Dropdown (visible only below lg) */}
          <div ref={dropdownRef} className="relative w-full lg:hidden z-30">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center justify-between w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-[14px] font-semibold text-slate-800 shadow-sm focus:outline-none hover:bg-slate-50 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                {currentCategoryData.iconClass ? (
                  <i className={currentCategoryData.iconClass} aria-hidden />
                ) : null}
                <span className="truncate">{currentCategoryData.label}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {currentCategoryData.statusText ? (
                  <span className={`text-[11px] font-medium ${currentCategoryData.statusClass}`}>
                    {currentCategoryData.statusText}
                  </span>
                ) : null}
                <i className={`ri-arrow-down-s-line text-slate-400 text-lg transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {isDropdownOpen && (
              <div className="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-40 max-h-[300px] overflow-y-auto">
                {visibleCategories.map((catKey) => {
                  const cat = DOC_CATEGORIES[catKey];
                  const active = selectedCategory === catKey;
                  const uploadedCount = uploadedCountForCategory(catKey);
                  const side = categorySidebarStatus({
                    files: uploadedCount > 0 ? Array.from({ length: uploadedCount }, () => ({})) : [],
                    reviewStatus: categoryReview[catKey] ?? 'pending',
                    multiple: cat.multiple,
                    optional: cat.optional,
                  });
                  const statusTitle = String(side.text ?? '');
                  const iconClass = categorySidebarIconClass(side, uploadedCount);
                  const showStatusText =
                    uploadedCount > 0 ||
                    side.variant === 'rejected' ||
                    (side.variant !== 'complete' && side.variant !== 'none' && side.variant !== 'awaiting');
                  return (
                    <button
                      key={catKey}
                      type="button"
                      onClick={() => {
                        setSelectedCategory(catKey);
                        setIsDropdownOpen(false);
                      }}
                      className={`flex w-full min-h-[44px] items-center gap-2.5 px-4 py-2.5 text-left transition-colors cursor-pointer ${active ? 'bg-slate-100 font-semibold' : 'hover:bg-slate-50'
                        }`}
                    >
                      {iconClass ? <i className={iconClass} aria-hidden /> : null}
                      <span className="flex-1 text-[13px] font-medium text-slate-800 truncate">
                        {sidebarLabel(catKey, getCategoryDisplayLabel(catKey, cat.label, prorrogaType))}
                      </span>
                      {showStatusText ? (
                        <span
                          className={`text-[10px] font-medium shrink-0 tabular-nums ${side.variant === 'rejected'
                              ? 'text-rose-600'
                              : uploadedCount > 0
                                ? 'text-emerald-700'
                                : 'text-slate-400'
                            }`}
                        >
                          {statusTitle}
                        </span>
                      ) : null}
                    </button>
                  );
                })}

                {archivedDocs.length > 0 && (
                  <>
                    <div className="my-1 border-t border-slate-100" />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCategory('archived');
                        setIsDropdownOpen(false);
                      }}
                      className={`flex w-full min-h-[44px] items-center gap-2.5 px-4 py-2.5 text-left transition-colors cursor-pointer ${selectedCategory === 'archived' ? 'bg-slate-100 font-semibold' : 'hover:bg-slate-50'
                        }`}
                    >
                      <i className="ri-archive-line text-slate-400 text-base shrink-0" />
                      <span className="flex-1 text-[13px] font-medium text-slate-800 truncate">
                        {t('advisorPortal.docArchivedTitle')}
                      </span>
                      <span className="text-[11px] text-slate-400 shrink-0">{archivedDocs.length}</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Left pane: category sidebar */}
          <nav className="hidden lg:block lg:space-y-1 lg:sticky lg:top-4 lg:overflow-visible bg-white border border-slate-200 rounded-xl p-1.5 self-start">
            {visibleCategories.map((catKey) => {
              const cat = DOC_CATEGORIES[catKey];
              const active = selectedCategory === catKey;
              const uploadedCount = uploadedCountForCategory(catKey);
              const side = categorySidebarStatus({
                files: uploadedCount > 0 ? Array.from({ length: uploadedCount }, () => ({})) : [],
                reviewStatus: categoryReview[catKey] ?? 'pending',
                multiple: cat.multiple,
                optional: cat.optional,
              });
              const statusTitle = String(side.text ?? '');
              const iconClass = categorySidebarIconClass(side, uploadedCount);
              const showStatusText =
                uploadedCount > 0 ||
                side.variant === 'rejected' ||
                (side.variant !== 'complete' && side.variant !== 'none' && side.variant !== 'awaiting');
              return (
                <button
                  key={catKey}
                  type="button"
                  onClick={() => setSelectedCategory(catKey)}
                  title={statusTitle}
                  className={`flex min-h-[44px] min-w-[10rem] items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors cursor-pointer lg:w-full lg:min-w-0 ${active ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                >
                  {iconClass ? <i className={iconClass} aria-hidden /> : null}
                  <span className="flex-1 text-[13px] font-medium text-slate-800 truncate">
                    {sidebarLabel(catKey, getCategoryDisplayLabel(catKey, cat.label, prorrogaType))}
                  </span>
                  {showStatusText ? (
                    <span
                      className={`text-[10px] font-medium shrink-0 tabular-nums ${side.variant === 'rejected'
                          ? 'text-rose-600'
                          : uploadedCount > 0
                            ? 'text-emerald-700'
                            : 'text-slate-400'
                        }`}
                    >
                      {statusTitle}
                    </span>
                  ) : null}
                </button>
              );
            })}
            {archivedDocs.length > 0 && (
              <>
                <div className="hidden my-1 border-t border-slate-100 lg:block" />
                <button type="button" onClick={() => setSelectedCategory('archived')}
                  className={`flex min-h-[44px] min-w-[10rem] items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors cursor-pointer lg:w-full lg:min-w-0 ${selectedCategory === 'archived' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}>
                  <i className="ri-archive-line text-slate-400 text-base shrink-0" />
                  <span className="flex-1 text-[13px] font-medium text-slate-800 truncate">{t('advisorPortal.docArchivedTitle')}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">{archivedDocs.length}</span>
                </button>
              </>
            )}
          </nav>

          {/* Right pane: category components */}
          <div className="space-y-6 min-w-0">
            {visibleCategories.filter((catKey) => catKey === selectedCategory).map((catKey) => {
              const catReview = categoryReview[catKey] ?? 'pending';
              const catReviewChange = (status: DocumentReviewStatus) => onCategoryReviewChange(catKey, status);

              if (catKey === 'passport') {
                return (
                  <IdentityCategory key={catKey}
                    docs={docs} onDocsChange={onDocsChange}
                    handleUpload={handleUpload}
                    passportIdFields={passportIdFields} onPassportIdChange={onPassportIdChange}
                    categoryReview={catReview} onCategoryReviewChange={catReviewChange}
                    onViewDocument={openViewerForDoc}
                  />
                );
              }
              if (catKey === 'residence') {
                return <ResidenceCategory key={catKey} docs={docs} onDocsChange={onDocsChange} categoryReview={catReview} onCategoryReviewChange={catReviewChange} onViewDocument={openViewerForDoc} />;
              }
              if (catKey === 'criminal') {
                return (
                  <CriminalCategory key={catKey}
                    criminalCertificates={criminalCertificates}
                    onCriminalCertificatesChange={onCriminalCertificatesChange}
                    countriesOfResidence={countriesOfResidence}
                    onCountriesChange={onCountriesChange}
                    categoryReview={catReview} onCategoryReviewChange={catReviewChange}
                    onViewCertificate={openViewerForCriminal}
                  />
                );
              }
              if (catKey === 'asylum') {
                return (
                  <AsylumCategory key={catKey}
                    docs={docs} onDocsChange={onDocsChange}
                    oarCaseNumber={oarCaseNumber}
                    onOarCaseNumberChange={onOarCaseNumberChange ?? (() => { })}
                    categoryReview={catReview} onCategoryReviewChange={catReviewChange}
                    onViewDocument={openViewerForDoc}
                  />
                );
              }
              if (catKey === 'employment') {
                return (
                  <EmploymentCategory
                    key={catKey}
                    docs={docs}
                    onDocsChange={onDocsChange}
                    employmentFields={employmentFields}
                    onEmploymentFieldsChange={onEmploymentFieldsChange}
                    categoryReview={catReview}
                    onCategoryReviewChange={catReviewChange}
                    onViewDocument={openViewerForDoc}
                  />
                );
              }
              if (catKey === 'family') {
                return <FamilyCategory key={catKey} docs={docs} onDocsChange={onDocsChange} categoryReview={catReview} onCategoryReviewChange={catReviewChange} onViewDocument={openViewerForDoc} />;
              }
              if (catKey === 'vulnerability') {
                return <VulnerabilityCategory key={catKey} docs={docs} onDocsChange={onDocsChange} categoryReview={catReview} onCategoryReviewChange={catReviewChange} onViewDocument={openViewerForDoc} />;
              }
              if (catKey === 'other') {
                return <OtherCategory key={catKey} docs={docs} onDocsChange={onDocsChange} categoryReview={catReview} onCategoryReviewChange={catReviewChange} onViewDocument={openViewerForDoc} />;
              }
              return null;
            })}

            {/* Archived */}
            {selectedCategory === 'archived' && archivedDocs.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-200">
                  <div className="w-9 h-9 flex items-center justify-center shrink-0">
                    <i className="ri-archive-line text-slate-400 text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900">Archived</h3>
                    <p className="text-[12px] text-slate-400">Removed documents, kept for reference</p>
                  </div>
                  <span className="text-[12px] text-slate-400 shrink-0">{archivedDocs.length} doc{archivedDocs.length !== 1 ? 's' : ''}</span>
                  <button onClick={() => setArchivedDocs([])} className="text-[12px] font-medium text-slate-400 hover:text-rose-500 cursor-pointer whitespace-nowrap ml-2">Delete all</button>
                </div>
                <div className="max-w-[720px] space-y-2">
                  {archivedDocs.map((doc) => (
                    <div key={doc.id} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden opacity-75">
                      <div className="flex flex-col gap-3 px-3.5 py-3 min-[420px]:flex-row min-[420px]:items-center">
                        <i className={`${getFileIcon(doc.uploadedFile ?? '')} text-slate-400 text-lg shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-slate-500 truncate">{doc.uploadedFile ?? doc.name}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">{DOC_CATEGORIES[doc.category]?.label ?? doc.category}{doc.uploadedAt && ` · ${formatDate(doc.uploadedAt)}`}</p>
                        </div>
                        <button
                          onClick={() => {
                            const filename = doc.uploadedFile ?? `${doc.name}.pdf`;
                            const blob = new Blob([`Archived document: ${filename}`], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = filename;
                            document.body.appendChild(a); a.click();
                            document.body.removeChild(a); URL.revokeObjectURL(url);
                            showToast('Download started');
                          }}
                          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 px-4 text-[12px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:border-slate-300 cursor-pointer whitespace-nowrap transition-colors"
                        >
                          <i className="ri-download-line text-base" /> Download
                        </button>
                        <button onClick={() => setArchivedDocs((prev) => prev.filter((d) => d.id !== doc.id))} className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer shrink-0">
                          <i className="ri-delete-bin-line text-lg" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DocumentViewerModal
        open={Boolean(viewerDoc || viewerCriminal)}
        title={viewerTitle}
        fileName={viewerFileName}
        previewUrl={viewerBlobUrl}
        mimeType={viewerMimeType}
        loading={viewerLoading}
        error={viewerError}
        zoom={viewerZoom}
        onDownload={handleViewerDownload}
        onAccept={() => handleViewerReview('accepted')}
        onDecline={() => handleViewerReview('rejected')}
        onClose={closeViewer}
      />
    </div>
  );
}
